// tslint:disable:no-trailing-whitespace
// tslint:disable:member-ordering
import { BaseEntity } from '../base-entity';
import { Account } from './account';
import { AccountPurchaseOrderLog } from './account-purchase-order-log';
import { ActiveStatus } from './active-status';
import { ApprovalTreeUserGroup } from './approval-tree-user-group';
import { Program } from './program';
import { ActiveStatusEnum } from './enums';

/// <code-import> Place custom imports between <code-import> tags
import { EntityPropertyAnnotation, EntityTypeAnnotation } from '../entity-type-annotations';
import { Validator } from 'breeze-client';
import * as _ from 'lodash';
/// </code-import>

/// <module-code> Place module level code between <module-code> tags

/// </module-code>

export class AccountPurchaseOrder extends BaseEntity  {

  /// <code> Place custom code between <code> tags
  isParent(): boolean {
    return this.parentAccountPurchaseOrderId == this.id;
  }

  // Next two calc require that all of the children/or parents are already loaded.
  getChildren(): AccountPurchaseOrder[] {
    return this.isParent()
      ? this.childAccountPurchaseOrders.filter((x) => x.id != this.parentAccountPurchaseOrderId)
      : ([] as AccountPurchaseOrder[]);
  }

  getAmtUsed(): number {
    if (this.isParent()) {
      return _.sum(this.getChildren().map((x) => x.getAmtUsed()));
    } else {
      return _.sum(this.accountPurchaseOrderLogs.map((x) => x.amt));
    }
  }

  getAmtAllocated(): number {
    if (!this.isParent()) return 0;
    return _.sum(this.getChildren().map((x) => x.amt));
  }

  static amtExceedsLimitValidator = new Validator(
    'amtExceedsValidator',
    (value: any, context) => {
      if (value == null) {
        return true;
      }
      const po = context?.entity as AccountPurchaseOrder;

      if (po.isParent()) return true;
      return po.amt >= po.getAmtUsed();
    },
    { message: () => `Purchase Order Amount cannot be less that that already used.` },
  );

  static amtExceedsParentValidator = new Validator(
    'amtExceedsParentValidator',
    (value: any, context) => {
      if (value == null) {
        return true;
      }
      const po = context?.entity as AccountPurchaseOrder;

      if (po.isParent()) {
        return po.amt > _.sum(po.getChildren().map((x) => x.amt));
      } else if (po.parentAccountPurchaseOrder != null) {
        const parentPo = po.parentAccountPurchaseOrder;
        const childSum = _.sum(parentPo.getChildren().map((x) => x.amt));
        return parentPo.amt > childSum;
      } else {
        return true; // no parent, so no validation needed
      }
    },
    { message: () => `Parent Purchase Order Amount must be greater than or equal to the sum of all child purchase order Amounts` },
  );

  static dateAfterValidator = new Validator(
    'endDateAfterStartDate',
    (value: any, context) => {
      if (value == null) {
        return true;
      }
      const startDate = context?.entity?.['startDate'];
      if (startDate == null) {
        return true;
      }
      if (startDate >= value) {
        return false;
      }
      return true;
    },
    { messageTemplate: '%displayName% must be greater than Start Date' },
  );

  static dateRequiredForBlanketValidator = new Validator(
    'dateRequiredForBlanket',
    (entity: any, context) => {
      const pi = entity as AccountPurchaseOrder;
      const isBlanketPurchaseOrder = pi['isBlanketPurchaseOrder'];
      const startDate = pi['startDate'];
      const endDate = pi['endDate'];
      if (isBlanketPurchaseOrder) {
        if (startDate == null || endDate == null) {
          return false;
        }
      } else {
        if (startDate != null || endDate != null) {
          return false;
        }
      }
      return true;
    },
    { messageTemplate: `Start Date and End Date are required on a 'Blanket' Purchase Order` },
  );

  static getEntityTypeAnnotation(): EntityTypeAnnotation {
    const propAnnotations = [
      new EntityPropertyAnnotation('startDate', {
        validators: [BaseEntity.dateRangeValidator(new Date(2010, 0, 1), null, true)],
      }),
      // TODO: add validation that this is after the start date.
      new EntityPropertyAnnotation('endDate', {
        validators: [BaseEntity.dateRangeValidator(new Date(2020, 0, 1), null, true), this.dateAfterValidator],
      }),
      new EntityPropertyAnnotation('amt', {
        displayName: 'Amount',
        // validators: [BaseEntity.greaterThanZeroValidator(true), this.amtExceedsLimitValidator, this.amtExceedsParentValidator],
        validators: [BaseEntity.greaterThanZeroValidator(true), this.amtExceedsParentValidator],
      }),
    ];

    return new EntityTypeAnnotation({
      validators: [this.dateRequiredForBlanketValidator],
      propertyAnnotations: propAnnotations,
    });
  }

  get nameAndStatus() {
    if (this.isExpired()) {
      return this.purchaseOrder + ' (Expired)';
    }

    if (this.activeStatusId === ActiveStatusEnum.Hold) {
      return this.purchaseOrder + ' (Held)';
    }

    if (this.activeStatusId === ActiveStatusEnum.Phaseout) {
      return this.purchaseOrder + ' (Phase-Out)';
    }

    if (this.activeStatusId === ActiveStatusEnum.Inactive) {
      return this.purchaseOrder + ' (Inactive)';
    }

    return this.purchaseOrder;
  }

  isExpired() {
    return this.endDate && this.endDate < new Date();
  }

  /**
   * Active means Active. Phaseout was admitted here and nowhere else, which made this the
   * loosest definition of a usable purchase order in the application: JobOrderRulesService
   * .validatePurchaseOrder raises 'The purchase order is not active' on anything but Active,
   * and the Collect-Organize-and-Place hold test does the same. A Phaseout purchase order
   * therefore passed this check and then tripped every gate downstream.
   */
  isCurrentAndActive() {
    return !this.isExpired() && this.activeStatusId === ActiveStatusEnum.Active;
  }
  /// </code>

  // Generated code. Do not place code below this line.
  /** Guid key */
  id!: string;
  /** Guid FK account */
  accountId!: string;
  /** Enum FK activeStatus */
  activeStatusId!: ActiveStatusEnum;
  /** Decimal */
  amt!: number;
  /** DateTime */
  endDate?: Date;
  /** Boolean */
  isBlanketPurchaseOrder!: boolean;
  /** Guid FK parentAccountPurchaseOrder */
  parentAccountPurchaseOrderId?: string;
  /** String(30) */
  purchaseOrder!: string;
  /** Binary */
  rowVersion!: any;
  /** DateTime */
  startDate?: Date;
  /** Decimal */
  warnIfWithinRate?: number;
  /** FK accountId */
  account!: Account;
  /** Inv FK accountPurchaseOrderId */
  accountPurchaseOrderLogs!: AccountPurchaseOrderLog[];
  /** FK activeStatusId */
  activeStatus!: ActiveStatus;
  /** Inv FK accountPurchaseOrderId */
  approvalTreeUserGroups!: ApprovalTreeUserGroup[];
  /** Inv FK parentAccountPurchaseOrderId */
  childAccountPurchaseOrders!: AccountPurchaseOrder[];
  /** FK parentAccountPurchaseOrderId */
  parentAccountPurchaseOrder!: AccountPurchaseOrder;
  /** Inv FK accountPurchaseOrderId */
  programs!: Program[];
}

